import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce, get } from 'lodash';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Spinner, Tooltip } from '~/components/ui';
import StyledInput from '~/components/ui/Form/StyledInput';

interface IbanDetails {
    valid: boolean;
    messages: string[];
    bankData: { bankCode: string; name: string; bic?: string; city?: string };
}

interface FormularIBANInputProps {
    name: string;
    label?: string;
    helperText?: string;
}

const FormularIBANInput = ({ name, label, helperText }: FormularIBANInputProps) => {
    const { register, formState: { errors }, getValues, setError, watch, clearErrors } = useFormContext();
    const error = get(errors, name);
    const [isLoading, setIsLoading] = useState(false);
    const [ibanDetails, setIbanDetails] = useState<IbanDetails>();
    const value = watch(name);
    const { ref, ...registerProps } = register(name);

    const validate = debounce((iban: string) => {
        if (!iban) return;
        setIsLoading(true);
        fetch(`https://openiban.com/validate/${iban}?getBIC=true`)
            .then((r) => r.ok ? r.json() : null)
            .then((data) => {
                if (data?.valid === false)
                    setError(name, { type: 'manual', message: 'Deine IBAN weist Fehler auf! Bitte überprüfe deine Eingaben.' });
                if (data?.valid === true) clearErrors(name);
                setIbanDetails(data);
            })
            .finally(() => setIsLoading(false));
    }, 350);

    useEffect(() => { validate(getValues(name)); }, [name, value]);

    const statusIcon = (
        <Tooltip placement="left" tooltip={isLoading ? 'Lade ...' : <IbanDetails details={ibanDetails} />}>
            <span className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-full">
                {isLoading
                    ? <Spinner size="sm" />
                    : <FontAwesomeIcon icon={ibanDetails?.valid ? faCheck : faTimes} className={ibanDetails?.valid ? 'tw-text-success' : 'tw-text-error'} />
                }
            </span>
        </Tooltip>
    );

    return (
        <StyledInput
            {...registerProps}
            ref={ref}
            name={name}
            label={label}
            isInvalid={!!error}
            helperText={(error?.message as string) || helperText}
            contentRight={statusIcon}
            fullWidth
        />
    );
};

const IbanDetails = ({ details }: { details?: IbanDetails }) => (
    <div className="tw-m-2 tw-min-w-[180px]">
        <p className="tw-mb-2 tw-font-semibold">Details</p>
        <div className="tw-flex tw-justify-between tw-gap-4 tw-text-sm">
            <span className="tw-text-accents-6">Status</span>
            <span className={details?.valid ? 'tw-text-success' : 'tw-text-error'}>
                {details?.valid ? 'VALIDE' : 'FEHLERHAFT'}
            </span>
        </div>
        <div className="tw-flex tw-justify-between tw-gap-4 tw-text-sm">
            <span className="tw-text-accents-6">BIC</span>
            <span>{details?.bankData?.bic || '–'}</span>
        </div>
        <div className="tw-flex tw-justify-between tw-gap-4 tw-text-sm">
            <span className="tw-text-accents-6">Bank</span>
            <span>{details?.bankData?.name || '–'}</span>
        </div>
    </div>
);

export default FormularIBANInput;
